<template>
  <main class="employee-payroll-details">
    <button-back />
    <div class="employee-payroll-details__title">
      <h1>Детализация расчета ЗП </h1>
      <p>Для сотрудника: {{employeeFullName}}</p>
    </div>
    <em v-if="hasFetchedDetails">
      Расчёт за период с
      <time v-bind:datetime="from">{{ fromTitle }}</time>
      по
      <time v-bind:datetime="to">{{ toTitle }}</time>
    </em>
    <div class="employee-payroll-details__table-wrapper">
      <table
        class="details-table"
        v-if="hasFetchedDetails"
      >
        <tfoot
          class="details-table__summary"
          v-if="hasSummaryLines"
        >
          <tr
            class="details-table__summary-line"
            v-bind:key="key"
            v-for="{ key, title, value } in summaryLines"
          >
            <th>{{ title }}</th>
            <td>{{ value }}</td>
          </tr>
        </tfoot>
        <thead>
          <tr
            class="details-table__head-line"
            v-bind:class="{ mini: headCells.length > 4 }"
          >
            <th
              class="details-table__head-cell"
              v-bind:key="key"
              v-for="{ key, title } in headCells"
            >{{ title }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            class="details-table__body-line"
            v-bind:key="key"
            v-for="{ cells, groupHeadKey, key } in bodyRows"
            v-bind:class="{ mini: cells.length > 4 }"
          >
            <td
              class="details-table__body-cell"
              v-bind:key="key"
              v-for="({ key, title }, cellIndex) in cells"
            >
              <label
                class="details-table__expansion"
                v-if="cellIndex === 0 && groupHeadKey">
                <input
                  type="checkbox"
                  v-bind:value="groupHeadKey"
                  v-model="expandedGroups">
                <span />
              </label>
              {{ title }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </main>
</template>
<script>
import ButtonBack from '@/components/ButtonBack.vue';

/**
 * @description Детализация расчета заработной платы сотрудника.
 * @type {Vue}
 */
export default {
  components: { ButtonBack },
  computed: {
    bodyRows() {
      return this.rows.flatMap(({ fields, groupHeadKey, groupKey }, rowIndex) => {
        const rowKey = `body-rows:${rowIndex}`;
        const row = {
          cells: fields.map((title, cellIndex) => ({
            key: `${rowKey}:cells:${cellIndex}`,
            title: title.replace(/;/g, '\n\n'),
          })),
          groupHeadKey,
          key: rowKey,
        };
        if (groupKey === '') {
          return [row];
        }
        return this.expandedGroups.includes(groupKey) ? [row] : [];
      });
    },
    /**
     * @description Имеет строки над таблицей.
     */
    hasSummaryLines() {
      return this.summaryLines.length !== 0;
    },
    headCells() {
      return this.columns.map((title, index) => ({
        key: `head-cells:${index}`,
        title,
      }));
    },
    /**
     * @description Итоги по расчету.
     */
    summaryLines() {
      return [
        ...this.activatedRules.map((title, index) => ({
          key: `summary-lines:rule-${index}`,
          title,
          value: 'да',
        })),
        ...(this.totalSalary ? [{
          key: 'summary-lines:salary',
          title: 'Сумма оклада за месяц',
          value: this.totalSalary,
        }] : []),
      ];
    },
  },
  created() {
    this
      .$payrollGateway
      .viewSlipDetails(this.calculationId, this.employeeId)
      .then((result) => {
        result.forEach((details) => {
          this.activatedRules = details.activated_rules;
          this.columns = details.columns;
          this.from = details.period_from;
          this.employeeFullName = details.employee_full_name;
          this.fromTitle = details.period_from_title;
          this.rows = details.rows.map(({
            fields,
            group_head_key: groupHeadKey,
            group_key: groupKey,
          }) => ({
            fields,
            groupHeadKey,
            groupKey,
          }));
          this.to = details.period_to;
          this.toTitle = details.period_to_title;
          this.totalSalary = details.total_salary;
        });
        this.hasFetchedDetails = true;
      });
  },
  data() {
    return {
      activatedRules: [],
      columns: [],
      expandedGroups: [],
      employeeFullName: '',
      from: '',
      fromTitle: '',
      hasFetchedDetails: false,
      rows: [],
      to: '',
      toTitle: '',
      totalSalary: '',
    };
  },
  name: 'ClinicPanelEmployeePayrollDetails',
  props: {
    calculationId: {
      required: true,
      type: String,
    },
    employeeId: {
      required: true,
      type: String,
    },
  },
};
</script>
<style scoped lang="scss">
.employee-payroll-details {
  display: flex;
  flex-direction: column;
  gap: 2rem;
  overflow: scroll;
  padding: 2rem 1rem;
  height: 100%;
  &__title {
    display:flex;
    flex-wrap: wrap;
    align-items: baseline;
    justify-content: left;
    gap: 10px;
  }

  &__table-wrapper {
    overflow-x: auto;
  }
}

.details-table {
  position: relative;
  border-collapse: collapse;
  font-size: .875rem;
  line-height: 1rem;
  overflow: auto;
  table-layout: fixed;
  width: 100%;
}

.details-table .details-table__body-cell {
  overflow: hidden;
  padding: 15px 8px;
  text-align: left;
  text-overflow: ellipsis;
  text-transform: none;
  white-space: pre-line;
}

.details-table .details-table__body-line {
  border-bottom: 1px solid var(--fill-color);
  &.mini {
    td:nth-child(1) {
      max-width: 130px;
      width: 130px;
    }
    td:nth-child(2) {
      max-width: 150px;
      width: 150px;
    }
  }
}

.details-table .details-table__expansion {
  position: relative;
}

.details-table .details-table__expansion>input {
  opacity: 0;
  position: absolute;
  z-index: -1;
}

.details-table .details-table__expansion>input:checked+span {
  background-position: top;
  transform: rotate(180deg);
}

.details-table .details-table__expansion>span {
  align-items: center;
  background-image: url("@/assets/img/checked/arrow-top-blue.svg");
  background-position: bottom;
  background-size: contain;
  cursor: pointer;
  display: inline-flex;
  height: 15px;
  margin: 0 17px 0 0;
  user-select: none;
  width: 15px;
}

.details-table .details-table__head-cell {
  position: sticky;
  top: 0;
  padding: 10px 8px;
  text-align: left;
  background-color: #ffffff;
  z-index: 1;
  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -2px;
    width: 100%;
    border-bottom: 2px solid var(--action-color);
  }
}

.details-table .details-table__head-line {
  border-bottom: 2px solid var(--action-color);
  font-weight: 700;
  &.mini {
    th:nth-child(1) {
      max-width: 130px;
      width: 130px;
    }
    th:nth-child(2) {
      max-width: 150px;
      width: 150px;
    }
  }
}

.details-table .details-table__summary {
  display: table-header-group;
  text-align: left;
}

.details-table .details-table__summary-line {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  width: max(100%, 18rem);
  margin-bottom: 5px;
  line-height: 1.4;
  &:last-child {
    margin-bottom: 10px;
  }
}

@media screen and (max-width: 600px) {
  .employee-payroll-details {
    .details-table  {
      width: auto;
    }
  }
}
</style>
